import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { CardPaymentSheet } from '@/nova/pages/wallet/components/CardPaymentSheet';
import { useCreditTopup } from '@/nova/hooks/useCreditTopup';

import { Button, Icon, Text } from '../../../../components';
import { BAR_COLORS, COLORS } from '../../../../theme/tokens';
import { currencySymbol, formatAmountWithCode } from '../../../../utils/money';
import { CREDIT_CURRENCY, CREDIT_TOPUP_USD_PRESETS, formatCredits, usdToCredits } from '../../../../utils/credits';
import {
  Body,
  Intro,
  PackGrid,
  PackCard,
  PackCredits,
  PackPrice,
  SelectedCheck,
  CustomRow,
  AmountInput,
  CustomUnit,
  CostHint,
  FundingNote,
  StatusText,
  ErrorRow,
  ErrorText,
  SuccessWrap,
  SuccessIcon,
  SuccessTitle,
  SuccessBody,
  SuccessNote,
  ScreenFade,
} from './BuyCreditsSheet.styles';

export type BuyCreditsSheetProps = {
  onClose: () => void;
  /** Fired after credits are successfully added (caller refetches / closes). */
  onSuccess: () => void;
  /** Optional credit amount to preselect (e.g. the shortfall the studio's 402 reported). */
  suggestedCredits?: number;
};

/** Fade-out duration before the body/footer is swapped, mirroring the wallet DepositModal. */
const SCREEN_FADE_MS = 150;

type Screen = 'pick' | 'success';

function useScreenFade(screen: Screen): { rendered: Screen; shown: boolean } {
  const [rendered, setRendered] = React.useState<Screen>(screen);
  const [shown, setShown] = React.useState(true);
  const timer = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (screen === rendered) return;
    setShown(false);
    timer.current = window.setTimeout(() => {
      setRendered(screen);
      setShown(true);
    }, SCREEN_FADE_MS);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [screen, rendered]);
  return { rendered, shown };
}

/** Money in the credit's currency, preformatted ("$25.00 USD"). Rendered as a data value, not translated. */
const money = (amount: number): string => `${currencySymbol(CREDIT_CURRENCY)}${formatAmountWithCode(amount, CREDIT_CURRENCY)}`;

/**
 * Buy-credits sheet. Pick how many credits to add (money-anchored suggestions or a custom amount), then
 * buy them: the owner-hub wallet mints a `deposit_ai_credits` invoice settled either from the MAIN wallet
 * (when it covers the cost) or by card (the card pays for the credits outright — no "top up Main first"
 * step). Once the AI_CREDITS balance moves, the sheet shows success. The balance itself lives on the
 * wallet (`state.ownerWallet.credits`); this sheet only drives the buy.
 */
export const BuyCreditsSheet = ({ onClose, onSuccess, suggestedCredits }: BuyCreditsSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  const [screen, setScreen] = React.useState<Screen>('pick');
  const [purchasedCredits, setPurchasedCredits] = React.useState(0);
  // The custom-amount field's raw text (empty when a preset is chosen).
  const [customText, setCustomText] = React.useState('');

  const topup = useCreditTopup(() => {
    // Fires once the AI_CREDITS balance actually moves (both rails). `credited` is the landed delta.
    setPurchasedCredits(Math.round(topupRef.current.credited ?? topupRef.current.credits));
    onSuccess();
    setScreen('success');
  });
  // Latest topup in a ref so the onPurchased callback (created once) reads the credits actually bought.
  const topupRef = React.useRef(topup);
  topupRef.current = topup;

  const { credits, setCredits, rate, moneyCost, canPayFromMain, settling, credited, stalled } = topup;

  // Money-anchored suggestions ($10/$25/… as their credit equivalent at the live rate).
  const presets = React.useMemo(
    () => CREDIT_TOPUP_USD_PRESETS.map((usd) => ({ usd, credits: usdToCredits(usd, rate) })),
    [rate],
  );

  // Preselect a suggested amount (the shortfall the studio's 402 reported) once.
  const primed = React.useRef(false);
  React.useEffect(() => {
    if (primed.current) return;
    primed.current = true;
    if (suggestedCredits && suggestedCredits > 0) setCredits(Math.round(suggestedCredits));
  }, [suggestedCredits, setCredits]);

  const pickPreset = React.useCallback(
    (presetCredits: number) => {
      setCustomText('');
      setCredits(presetCredits);
    },
    [setCredits],
  );

  const onCustomChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.currentTarget.value.replace(/[^0-9]/g, '');
      setCustomText(digits);
      setCredits(Math.max(0, Number(digits) || 0));
    },
    [setCredits],
  );

  const { rendered, shown } = useScreenFade(screen);

  // ---- body + footer for the rendered screen ----
  let body: React.ReactElement;
  let footer: React.ReactElement;

  if (rendered === 'success') {
    body = (
      <SuccessWrap>
        <SuccessIcon aria-hidden="true">
          <Icon name="check" size={28} color={COLORS.badgeGreen} />
        </SuccessIcon>
        <SuccessTitle tag="span" text="Credits added" />
        <SuccessBody
          tag="span"
          text={
            purchasedCredits === 1
              ? '{{credits}} credit was added to your balance.'
              : '{{credits}} credits were added to your balance.'
          }
          placeholders={{ credits: formatCredits(purchasedCredits) }}
        />
        <SuccessNote tag="span" text="It may take a few moments for your balance to update." />
      </SuccessWrap>
    );
    footer = <Button variant="primary" fullWidth label={t('Done')} onClick={dismiss} />;
  } else {
    body = (
      <Body>
        <Intro tag="span" text="Choose how many credits to add. Pay from your Main wallet or by card." />
        <PackGrid>
          {presets.map((preset) => {
            const selected = customText === '' && credits === preset.credits;
            return (
              <PackCard
                key={preset.usd}
                type="button"
                $selected={selected}
                disabled={topup.purchasing}
                onClick={() => pickPreset(preset.credits)}
              >
                <PackCredits
                  $selected={selected}
                  tag="span"
                  text={preset.credits === 1 ? '{{credits}} credit' : '{{credits}} credits'}
                  placeholders={{ credits: formatCredits(preset.credits) }}
                />
                <PackPrice>{money(preset.usd)}</PackPrice>
                {selected ? (
                  <SelectedCheck aria-hidden="true">
                    <Icon name="check" size={12} color={COLORS.onAccent} />
                  </SelectedCheck>
                ) : null}
              </PackCard>
            );
          })}
        </PackGrid>

        <CustomRow>
          <AmountInput
            value={customText}
            onChange={onCustomChange}
            inputMode="numeric"
            placeholder={t('Custom amount')}
            aria-label={t('Custom credit amount')}
            disabled={topup.purchasing}
          />
          <CustomUnit text="credits" />
          {credits > 0 ? <CostHint>{money(moneyCost)}</CostHint> : null}
        </CustomRow>

        {settling ? (
          <StatusText tag="span" text="Updating your balance…" />
        ) : stalled ? (
          <ErrorRow role="alert">
            <Icon name="warning" size={14} color={BAR_COLORS.danger} />
            <ErrorText>{t('This is taking a little longer than usual.')}</ErrorText>
          </ErrorRow>
        ) : topup.purchaseError ? (
          <ErrorRow role="alert">
            <Icon name="warning" size={14} color={BAR_COLORS.danger} />
            <ErrorText>{topup.purchaseError}</ErrorText>
          </ErrorRow>
        ) : credits > 0 ? (
          // Literal text props (not a ternary in `text=`) so the i18n extractor harvests both.
          canPayFromMain ? (
            <FundingNote tag="span" text="Paid from your Main wallet." />
          ) : (
            <FundingNote tag="span" text="You'll pay by card." />
          )
        ) : null}
      </Body>
    );

    if (stalled) {
      footer = <Button variant="primary" fullWidth label={t('Check again')} onClick={topup.retrySync} />;
    } else {
      const primaryLabel = topup.purchasing || settling
        ? t('Please wait…')
        : credits === 1
          ? t('Buy {{n}} credit', { n: formatCredits(credits) })
          : credits > 0
            ? t('Buy {{n}} credits', { n: formatCredits(credits) })
            : t('Buy credits');
      footer = (
        <>
          <Button
            variant="primary"
            icon="plus"
            fullWidth
            disabled={credits <= 0 || settling}
            label={primaryLabel}
            onClick={topup.purchase}
          />
          {/* When the Main wallet covers it, the primary pays from Main — offer card as the alternative. */}
          {canPayFromMain && credits > 0 && !settling ? (
            <Button variant="text" fullWidth label={t('Pay by card instead')} onClick={topup.openCard} />
          ) : null}
        </>
      );
    }
  }

  return (
    <>
      <Sheet
        title={t('Buy AI credits')}
        onClose={onClose}
        closeHandle={closeHandle}
        busy={topup.purchasing}
        animateResize
        footer={<ScreenFade $shown={shown}>{footer}</ScreenFade>}
      >
        <ScreenFade $shown={shown}>{body}</ScreenFade>
      </Sheet>

      {topup.cardOpen ? (
        <CardPaymentSheet
          amountLabel={money(moneyCost)}
          createIntent={topup.mintCardIntent}
          onSuccess={topup.onCardPaid}
          onClose={topup.closeCard}
        />
      ) : null}
    </>
  );
};

export default BuyCreditsSheet;
